import stringify from 'fast-json-stable-stringify';
import { contextClient } from '../../client';
import { getChipValue, getValuesDefinitions } from '../../filters/chips';
import { ContextSettings, EventName, Filters } from '../../types';
import { InsightCollectionDataType, InsightData } from './types';
import { CollectionOptions } from '../../types/content/content.types';
import { handlePageElementViewed } from '../../analytics/trackPageElementViewed.hook';
import cache from '../../cache/cache';
import { getAccessValueForTracking } from '../../analytics/access';
import { getPropositionFromContextURL, isMultiCell, removeContextItemsByKey, removeDataItemsByKey } from './utils';
import { queryClient } from '../../page/ReactQueryProvider';

const metadata = {
  tracking: {
    paramsMap: {
      scKpis: 'elementKPI',
    },
  },
};

export async function getInsightInfo(insight: InsightData, collection: CollectionOptions) {
  if (!insight?.corePageSettings.collection.url) {
    return {
      chartName: collection.chartName,
      tags: [],
      trackingTags: {},
    };
  }

  const origin = window.location.origin;

  let baseURLToUse: string;
  try {
    const url = new URL(insight.corePageSettings.collection.url);
    baseURLToUse = origin + url.pathname;
  } catch {
    // Handle relative URLs or invalid URLs
    const urlPath = insight.corePageSettings.collection.url.startsWith('/')
      ? insight.corePageSettings.collection.url
      : `/${insight.corePageSettings.collection.url}`;
    baseURLToUse = origin + urlPath;
  }

  const { proposition } = getPropositionFromContextURL(insight.corePageSettings.collection.url);
  const contextPayload = {
    ...insight.paramsForContext,
    isLive: insight.dataType === InsightCollectionDataType.live ? [String(true)] : undefined,
    proposition: [proposition],
  };
  const contextKey = stringify(contextPayload);
  const context = await queryClient.fetchQuery({
    queryKey: ['insight-context', contextKey],
    queryFn: () => {
      return contextClient.getContext(baseURLToUse, contextPayload);
    },
    staleTime: Infinity,
  });

  const componentSettings = insight.componentSettings;
  const timeFrame = context.settings.values.timeFrame?.[0]?.value.split(':')[0] || '';
  const { paramsForContext, paramsForData } = insight;
  if (isMultiCell(paramsForContext)) {
    removeContextItemsByKey({ paramsForContext });
    removeDataItemsByKey({ paramsForData });
  }
  const [data, trackPageEvent, access] = await Promise.all([
    cache('data'),
    cache('trackPageEvent'),
    getAccessValueForTracking(proposition, insight.paramsForData.cell, timeFrame),
  ]);

  const params: any = handlePageElementViewed(context, data, componentSettings, metadata);
  const eventParams = trackPageEvent?.(EventName.PageElementViewed, params, { fireEvent: false, context });
  eventParams.pageName = collection.chartName;
  eventParams.elementName = collection.chartName;
  eventParams.access = Array.from(new Set(access));
  return {
    chartName: collection.chartName,
    tags: [...getSettingsTags(context.settings), ...getFilterTags(context.filters)],
    trackingTags: eventParams,
  };
}

const getFilterTags = ({ sections, values }: Filters) => {
  const sectionsWithDefinitions = getValuesDefinitions(sections, values);
  return sectionsWithDefinitions
    .flatMap(([_section, definitions]) => definitions)
    .map(def => ({
      key: def.label,
      value: getChipValue(def, values),
    }));
};

const getSettingsTags = ({ definitions }: ContextSettings) =>
  definitions.map(def => ({
    key: def.labels.identifier,
    value: def.labels.label,
  }));
